import React, { useState } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDeleteUserMutation } from '../../../store/user/user.api';

interface Props {
   userEmail: string;
   onHide: () => void;
}

const DeleteUserDialog = ({ userEmail, onHide }: Props) => {
   const [translate] = useTranslation(['common']);
   const [deleteUser, { isLoading }] = useDeleteUserMutation();
   const [error, setError] = useState<string | undefined>(undefined);

   const onDelete = async () => {
      setError(undefined);
      try {
         await deleteUser(userEmail).unwrap();
         onHide();
      } catch (deleteError) {
         const data = (deleteError as { data?: { message?: unknown } | string })?.data;
         const message = typeof data === 'object' ? data?.message : undefined;
         setError(
            typeof message === 'string' && message.length > 0
               ? message
               : String(translate('common:profile.deleteUserFailed')),
         );
      }
   };

   return (
      <Modal
         show
         onHide={isLoading ? undefined : onHide}
         backdrop={isLoading ? 'static' : true}
         keyboard={!isLoading}
         aria-labelledby='delete-user-title'
      >
         <Modal.Header closeButton={!isLoading}>
            <Modal.Title id='delete-user-title'>
               {translate('common:modal.deleteUserTitle')}
            </Modal.Title>
         </Modal.Header>
         <Modal.Body>
            <p>{translate('common:modal.deleteUserText', { userEmail })}</p>
            <p className='mb-0 text-muted'>{translate('common:modal.deleteUserHistory')}</p>
            {error && (
               <Alert variant='danger' className='mt-3 mb-0'>
                  {error}
               </Alert>
            )}
         </Modal.Body>
         <Modal.Footer>
            <Button variant='outline-secondary' onClick={onHide} disabled={isLoading}>
               {translate('common:common.cancel')}
            </Button>
            <Button variant='danger' onClick={onDelete} disabled={isLoading}>
               {isLoading && (
                  <Spinner animation='border' size='sm' className='me-2' aria-hidden='true' />
               )}
               {translate(isLoading ? 'common:profile.deletingUser' : 'common:common.delete')}
            </Button>
         </Modal.Footer>
      </Modal>
   );
};

export default DeleteUserDialog;
